import { useCallback, useEffect, useRef } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { AnswerView } from './blocks/index.js';
import { PERIOD_OPTIONS, SCOPE_LABELS } from './sample.js';
import {
  AnalyzingCard,
  ClarifyCard,
  ConfirmHeavyCard,
  DeniedNote,
  ErrorCard,
  OutOfTokensBar,
  StoppedNote,
} from './states/index.js';
import type {
  AnalyzingMessage,
  AnswerMessage,
  AssistantMessage,
  ChatMessage,
  ClarifyMessage,
  ClarifyOption,
  ConfirmHeavyMessage,
  DrillAction,
  ErrorMessage,
  UserMessage,
} from './types.js';

/** Callbacks of the conversational / blocking cards (frames E, G, H, J). */
export interface StreamStateActions {
  readonly onChooseOption: (message: ClarifyMessage, option: ClarifyOption) => void;
  readonly onConfirmHeavy: (message: ConfirmHeavyMessage) => void;
  readonly onCancelHeavy: (message: ConfirmHeavyMessage) => void;
  readonly onStop: (message: AnalyzingMessage) => void;
  readonly onRetry: (message: ErrorMessage) => void;
  /** Puts a question's text back into the composer. */
  readonly onEditPrompt: (text: string) => void;
}

export interface MessageStreamProps {
  readonly messages: readonly ChatMessage[];
  readonly lastAnswerId: string | null;
  readonly onDrill: (action: DrillAction, from: AnswerMessage) => void;
  readonly onFocusRecord: (label: string) => void;
  readonly stateActions: StreamStateActions;
  /** `${base}/ai/wallet` - target of the «خرید توکن» button (frame I). */
  readonly walletHref: string;
  /** Id of the active conversation — switching it resets the scroll position. */
  readonly conversationKey: string | null;
  /** Display name of the person asking — labels their bubbles. */
  readonly userName: string;
}

/** DOM id of a message — the context panel's path steps scroll to it. */
export function messageDomId(messageId: string): string {
  return `ai-msg-${messageId}`;
}

/**
 * Scrolls `target` to the top of its own scroll container only — unlike
 * `scrollIntoView` it never scrolls the page behind the workspace.
 */
export function scrollWithin(scroller: HTMLElement, target: HTMLElement, behavior: ScrollBehavior) {
  const delta = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  scroller.scrollTo({ top: scroller.scrollTop + delta - SCROLL_PADDING_PX, behavior });
}
const SCROLL_PADDING_PX = 12;

/** Who is speaking: a small avatar and the name, above every bubble. */
function Speaker({ name, role }: { readonly name: string; readonly role: 'user' | 'assistant' }) {
  return (
    <div className={`ai-speaker ai-speaker--${role}`}>
      <span className="ai-speaker__avatar" aria-hidden="true">
        {role === 'assistant' ? <Icon name="star" size="sm" /> : name.trim().charAt(0) || '؟'}
      </span>
      <span className="ai-speaker__name">{name}</span>
      <span className="ai-speaker__role">{role === 'assistant' ? 'دستیار' : 'شما'}</span>
    </div>
  );
}

function UserBubble({ message, userName }: { message: UserMessage; userName: string }) {
  const period = PERIOD_OPTIONS.find((option) => option.id === message.period);
  const scope = message.scope.map((key) => SCOPE_LABELS[key]).join('، ');
  return (
    <div className="ai-msg ai-msg--user" id={messageDomId(message.id)}>
      <Speaker name={userName} role="user" />
      <div className="ai-bubble">{message.text}</div>
      {message.scope.length > 0 ? (
        <div className="ai-msg__meta">
          {scope}
          {period ? ` · ${period.label}` : ''}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Exhaustive switch over the assistant message kinds. Adding a kind to
 * `AssistantMessage` (types.ts) makes `assertNever` fail to compile until a
 * case is added here.
 */
function renderAssistant(
  message: AssistantMessage,
  props: Pick<MessageStreamProps, 'lastAnswerId' | 'onDrill' | 'onFocusRecord' | 'walletHref'> & {
    actions: StreamStateActions;
    busy: boolean;
    typing: boolean;
    onProgress: () => void;
  },
) {
  switch (message.kind) {
    case 'answer':
      return (
        <AnswerView
          message={message}
          onDrill={props.onDrill}
          onFocusRecord={props.onFocusRecord}
          isLatest={message.id === props.lastAnswerId}
          busy={props.busy}
          typing={props.typing}
          onProgress={props.onProgress}
        />
      );
    case 'analyzing':
      return (
        <AnalyzingCard
          message={message}
          onStop={message.stoppable === false ? undefined : props.actions.onStop}
        />
      );
    case 'stopped':
      return <StoppedNote />;
    case 'clarify':
      return (
        <ClarifyCard message={message} onChoose={props.actions.onChooseOption} busy={props.busy} />
      );
    case 'confirm-heavy':
      return (
        <ConfirmHeavyCard
          message={message}
          onConfirm={props.actions.onConfirmHeavy}
          onCancel={props.actions.onCancelHeavy}
          busy={props.busy}
        />
      );
    case 'out-of-tokens':
      return (
        <div className="sk-card ai-state ai-state--wide">
          <OutOfTokensBar
            balance={message.balance}
            estimate={message.estimate}
            canPurchase={message.canPurchase}
            walletHref={props.walletHref}
          />
        </div>
      );
    case 'error':
      return (
        <ErrorCard
          message={message}
          onRetry={props.actions.onRetry}
          onEdit={props.actions.onEditPrompt}
          busy={props.busy}
        />
      );
    case 'denied':
      return <DeniedNote message={message} />;
    default:
      return assertNever(message);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled assistant message kind: ${JSON.stringify(value)}`);
}

export function MessageStream({
  messages,
  lastAnswerId,
  onDrill,
  onFocusRecord,
  stateActions,
  walletHref,
  conversationKey,
  userName,
}: MessageStreamProps) {
  const streamRef = useRef<HTMLDivElement | null>(null);
  // Answers that arrive after the conversation was opened are typed out; ones already
  // there (history) appear whole.
  const known = useRef<{ key: string | null; ids: Set<string> }>({ key: null, ids: new Set() });
  if (known.current.key !== conversationKey) {
    known.current = { key: conversationKey, ids: new Set(messages.map((m) => m.id)) };
  }
  const busy = messages.some((m) => m.role === 'assistant' && m.kind === 'analyzing');

  // Follow the conversation like ChatGPT / Claude: a new question and the answer being typed keep
  // the end in view, unless the reader scrolled up to read — then we leave them where they are.
  // Opening another conversation jumps, no animation.
  const stick = useRef(true);
  const lastKey = useRef<string | null>(null);
  const follow = useCallback(() => {
    const scroller = streamRef.current?.parentElement;
    if (scroller && stick.current) scroller.scrollTop = scroller.scrollHeight;
  }, []);

  useEffect(() => {
    const scroller = streamRef.current?.parentElement;
    if (!scroller) return;
    const onScroll = () => {
      stick.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80;
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const scroller = streamRef.current?.parentElement;
    if (!scroller) return;
    const switched = lastKey.current !== conversationKey;
    lastKey.current = conversationKey;
    const last = messages[messages.length - 1];
    // A new question always brings the reader back to the end.
    if (switched || (last && last.role === 'user')) stick.current = true;
    if (stick.current) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: switched ? 'auto' : 'smooth' });
    }
  }, [messages, conversationKey]);

  return (
    <div ref={streamRef} className="ai-stream" role="log" aria-live="polite" aria-label="گفتگو">
      {messages.map((message) =>
        message.role === 'user' ? (
          <UserBubble key={message.id} message={message} userName={userName} />
        ) : (
          <div key={message.id} className="ai-msg ai-msg--assistant" id={messageDomId(message.id)}>
            <Speaker name="دستیار هوشمند" role="assistant" />
            {renderAssistant(message, {
              typing: !known.current.ids.has(message.id),
              onProgress: follow,
              lastAnswerId,
              onDrill,
              onFocusRecord,
              walletHref,
              actions: stateActions,
              busy,
            })}
          </div>
        ),
      )}
    </div>
  );
}
